/**
 * Building a repo's tensor inventory out of the byte layer plus the Hub layer.
 *
 * Cost of a full inventory, regardless of model size: two Range requests per
 * safetensors file (8 bytes, then the JSON header), plus one small whole-file
 * read of the shard index on sharded repos. A 200 GB model and a 200 MB model
 * cost within a rounding error of the same.
 */

import { readGgufInventory } from "../gguf/read-inventory.js";
import {
  type GgufVariant,
  autoGgufVariant,
  findGgufVariants,
  variantAsComponent,
} from "../gguf/variants.js";
import { type RepoComponent, autoComponent, findComponents } from "../hf/components.js";
import { type ResolvedRepo, fetchRange, fileUrl } from "../hf/hub.js";
import type { FileProvenance } from "../hf/provenance.js";
import {
  HEADER_LENGTH_PREFIX_BYTES,
  type TensorEntry,
  parseHeaderJson,
  parseHeaderLength,
  parseShardIndex,
} from "./header.js";

/**
 * A repo holds more than one model and did not say which to read.
 *
 * Carried as a typed error rather than an error string because the list *is*
 * the answer: a pipeline repo has no primary component to fall back on, and
 * guessing one would open a VAE for someone who came to look at a transformer.
 * The caller renders the choice.
 */
/**
 * The repo is readable and simply is not a format this tool reads.
 *
 * **A refusal, not a failure**, and it needs a type for the same reason
 * `ComponentChoiceRequired` does: the difference is invisible in a string, and
 * the caller has to tell them apart to choose a tone. Nothing went wrong here —
 * the file list was fetched, understood, and found to hold CoreML or ONNX or a
 * PyTorch pickle. Presenting that as "Error:" told a visitor their repo was
 * broken when the honest sentence is that we do not read that format yet.
 *
 * This project already takes that position everywhere else: the picker is "a
 * question, not a failure", and `explainUndecodable` was rewritten so the
 * limitation "is stated as ours rather than as a property of the tensor". The
 * loader was the one path still calling its own limits the visitor's problem.
 *
 * Raised by the owner after a CoreML repo, 2026-08-23.
 */
export class UnsupportedFormat extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UnsupportedFormat";
  }
}

export class ComponentChoiceRequired extends Error {
  readonly components: readonly RepoComponent[];
  /**
   * What the choice is *between*, which is not the same question twice.
   *
   * A safetensors pipeline holds different models — a text encoder beside a
   * transformer beside a VAE — with unrelated layer stacks. A GGUF repo holds
   * one model many times, at precisions from 2 to 16 bits. The message and the
   * picker both said "models" for both, which told a reader that
   * `unsloth/Qwen3.6-27B-MTP-GGUF` contains twenty-two subjects when it
   * contains one.
   */
  readonly kind: "models" | "quantizations";

  constructor(
    repoId: string,
    components: readonly RepoComponent[],
    kind: "models" | "quantizations" = "models",
  ) {
    super(
      kind === "quantizations"
        ? `${repoId} offers ${components.length} quantizations of one model. Pick one: ${components
            .map((c) => c.label)
            .join(", ")}`
        : `${repoId} holds ${components.length} models. Pick one: ${components
            .map((c) => c.label)
            .join(", ")}`,
    );
    this.name = "ComponentChoiceRequired";
    this.components = components;
    this.kind = kind;
  }
}

/**
 * The one model in the repo, or a refusal that says which kind it is.
 *
 * The three outcomes are deliberately distinct. Nothing readable is a fact
 * about the repo; several readable is a question for the visitor; exactly one
 * needs no interaction at all — which is what four of the nine refused repos
 * turned out to be, an ordinary model one directory down.
 */
function soleComponent(repo: ResolvedRepo): RepoComponent {
  const components = findComponents(repo.files);
  if (components.length === 0) {
    throw new Error(
      `${repo.repoId} has no safetensors or GGUF files at revision ${repo.sha.slice(0, 7)}. PyTorch .bin repos aren't supported yet.`,
    );
  }
  const auto = autoComponent(components);
  if (!auto) throw new ComponentChoiceRequired(repo.repoId, components);
  return auto;
}

/**
 * The GGUF variant to read, or a refusal that says which kind it is.
 *
 * Separate from `soleComponent` because the question is different: that one asks
 * *which model*, this asks *which quantization of the one model*. Its refusal is
 * the same `ComponentChoiceRequired`, so one picker serves both.
 */
function soleVariant(repo: ResolvedRepo): GgufVariant {
  const variants = findGgufVariants(repo.files);
  const auto = autoGgufVariant(variants);
  if (!auto) {
    throw new ComponentChoiceRequired(
      repo.repoId,
      variants.map(variantAsComponent),
      "quantizations",
    );
  }
  return auto;
}

/** A tensor plus the shard it lives in and its absolute byte range in that shard. */
export interface InventoryTensor extends TensorEntry {
  readonly file: string;
  /** Absolute offsets within `file` — `dataStart` already folded in. */
  readonly absoluteStart: number;
  readonly absoluteEnd: number;
}

export interface Inventory {
  readonly repo: ResolvedRepo;
  readonly tensors: readonly InventoryTensor[];
  readonly files: readonly string[];
  /**
   * Which model in the repo this is: the directory prefix, `""` at the root.
   *
   * Part of the inventory rather than derived by callers because it is what
   * distinguishes two otherwise-identical reads of one commit — the cache key
   * needs it, and so does any link that wants to reopen this exact view.
   */
  readonly component: string;
  readonly sharded: boolean;
  /**
   * What the **file itself** says about where it came from, when it says
   * anything. GGUF only — safetensors has no in-file equivalent, since its
   * `__metadata__` names the producing framework and nothing about lineage.
   *
   * **Extracted, not carried.** The obvious move is to keep the GGUF metadata
   * map, which is already parsed and holds these keys. It is the wrong one, and
   * not for size — the parser stores an array as its element count, so the
   * retained map is tens of entries, not the vocabulary.
   *
   * The reason is that the map's *contents* are whatever `KEEP_PREFIXES` in
   * `gguf/header.ts` happens to retain, a list that exists to bound parser
   * memory and has nothing to do with this. `Inventory` is the type the
   * IndexedDB cache stores, so carrying the map would tie the cache's schema to
   * that list: widening it to keep one new architecture key would change what
   * every cached entry holds, with no version bump and nothing to notice.
   *
   * Two declared fields are a contract. A parser artifact is not.
   */
  readonly fileProvenance?: FileProvenance;
  /**
   * Elements as the header declares them, summed. **Not the parameter count.**
   *
   * For a packed payload these differ by the packing factor: one `I32` element
   * can hold ten three-bit weights. This was called `totalParams` and fed the
   * status line, which is how `openai/gpt-oss-20b` came to report 11.96B on
   * screen. Renamed rather than merely documented — a field named `totalParams`
   * is a trap that gets re-entered, and the count someone wants is
   * `modelParamCount` in `analysis/anatomy.ts`.
   */
  readonly storedElements: number;
  readonly totalBytes: number;
  /** Bytes and params grouped by dtype — the "where do the bytes live" ledger. */
  readonly byDtype: readonly DtypeTally[];
}

export interface DtypeTally {
  readonly dtype: string;
  readonly tensors: number;
  /**
   * Raw `numel` — what this dtype physically holds, **not** a parameter count.
   *
   * Named `storedElements` for the same reason `Inventory.storedElements` is:
   * a field called `params` that is not the parameter count is precisely how
   * this defect keeps coming back. It was called that, and on
   * `openai-community/gpt2` it put **137M** in a column headed `params`
   * directly under a header reading **124M** — the page contradicting itself,
   * which is the failure the one-source-of-truth rule exists to stop.
   */
  readonly storedElements: number;
  readonly bytes: number;
}

/**
 * Read the header of one safetensors file: two Range requests, no payload.
 */
export async function readFileInventory(
  repo: ResolvedRepo,
  path: string,
): Promise<readonly InventoryTensor[]> {
  const url = fileUrl(repo, path);

  const lengthBytes = await fetchRange(url, 0, HEADER_LENGTH_PREFIX_BYTES);
  const headerLength = parseHeaderLength(lengthBytes);

  const headerBytes = await fetchRange(
    url,
    HEADER_LENGTH_PREFIX_BYTES,
    HEADER_LENGTH_PREFIX_BYTES + headerLength,
  );
  // The header is UTF-8 on the wire; decoding with TextDecoder rather than
  // assuming latin-1 matters for any repo with a non-ASCII tensor name.
  const json = new TextDecoder("utf-8").decode(headerBytes);
  const parsed = parseHeaderJson(json, headerLength);

  return parsed.tensors.map((t) => ({
    ...t,
    file: path,
    absoluteStart: parsed.dataStart + t.dataOffsets[0],
    absoluteEnd: parsed.dataStart + t.dataOffsets[1],
  }));
}

/**
 * Build the whole repo's inventory.
 *
 * Sharded repos are detected from the file list rather than by probing for a
 * 404, so this makes no speculative requests. The shards are read sequentially
 * on purpose: `fetchRange`'s concurrency cap would let four run at once, but a
 * sharded repo can have dozens of shards and firing them all off is exactly the
 * "no crawling" line the plan draws — a header read per shard, in order, is
 * still only two small requests each.
 */
export interface InventoryProgress {
  /** Shard headers read so far. */
  readonly done: number;
  readonly total: number;
  readonly file: string;
  /**
   * Real bytes read so far, when the format's index costs enough to say so.
   *
   * Present for GGUF and absent for safetensors, and that asymmetry is the
   * honest one: a safetensors header is ~2 KB per shard, where a GGUF index sits
   * behind the tokenizer at a median 10.46 MiB. "Metadata-only is nearly free"
   * stays true of one and stops being true of the other, and this is what lets
   * the status line say so with a real number rather than an estimate — a
   * forecast was tried and came out 66% low (plan §4).
   */
  readonly bytesRead?: number | undefined;
}

export async function readInventory(
  repo: ResolvedRepo,
  onProgress?: (progress: InventoryProgress) => void,
  component?: RepoComponent,
): Promise<Inventory> {
  /*
   * Format discovery, and it is two lines because `Inventory` is
   * format-agnostic: a reader that fills the same structure inherits the tensor
   * list, byte ledger, compare mode, path URLs, cache and share cards without
   * any of them learning that GGUF exists.
   *
   * safetensors wins a tie. A repo carrying both is a safetensors model with
   * GGUF conversions beside it — that is the direction conversions go, never the
   * other way — and the safetensors is the artifact the publisher trained.
   */
  if (
    findComponents(repo.files).length === 0 &&
    findGgufVariants(repo.files).length > 0
  ) {
    const variant = component
      ? (findGgufVariants(repo.files).find((v) => v.key === component.prefix) ??
        soleVariant(repo))
      : soleVariant(repo);
    return readGgufInventory(repo, variant, onProgress);
  }

  const chosen = component ?? soleComponent(repo);

  let files: string[];
  if (chosen.indexFile) {
    const indexUrl = fileUrl(repo, chosen.indexFile);
    // The index is a small JSON file with no length prefix to range against, so
    // this is the one whole-file read in the app. It is kilobytes, not gigabytes.
    const response = await fetch(indexUrl);
    if (!response.ok) {
      throw new Error(`Could not read ${chosen.indexFile}: HTTP ${response.status}`);
    }
    // An index names its shards relative to its own directory, so a nested
    // component's shards need the prefix put back. Guarded rather than
    // unconditional: an index that already writes full paths must not get the
    // prefix twice.
    files = [...parseShardIndex(await response.text()).shards].map((shard) =>
      shard.startsWith(chosen.prefix) ? shard : `${chosen.prefix}${shard}`,
    );
  } else {
    files = [chosen.singleFile as string];
  }

  const tensors: InventoryTensor[] = [];
  for (const [index, file] of files.entries()) {
    // Reported per shard rather than at the end: a frontier-scale repo has
    // hundreds of shards, and at 4-in-flight that is tens of seconds during
    // which a silent page looks broken. Observed on a real repo — see the plan's
    // field-report note. The counter is the near-term fix; lazy per-shard
    // hydration is the eventual one.
    onProgress?.({ done: index, total: files.length, file });
    tensors.push(...(await readFileInventory(repo, file)));
  }
  onProgress?.({ done: files.length, total: files.length, file: "" });

  return {
    repo,
    tensors,
    files,
    component: chosen.prefix,
    sharded: chosen.indexFile !== undefined,
    storedElements: tensors.reduce((sum, t) => sum + t.numel, 0),
    totalBytes: tensors.reduce((sum, t) => sum + t.byteLength, 0),
    byDtype: tallyByDtype(tensors),
  };
}

/**
 * Group the inventory by dtype, heaviest first.
 *
 * Cheap to compute and surprisingly clarifying — it is how you see at a glance
 * that a "4-bit" repo is really half f16 scales, or that the embedding table is
 * a third of the file.
 *
 * ## `params` here is raw `numel`, and that is correct rather than overlooked
 *
 * This is the second aggregate over unfolded element counts, and the sweep that
 * found the status-line defect had to rule on it (reviewer, 2026-08-10: every
 * display reads the folded view *or declares why not*). This is a declaration.
 *
 * It cannot fold, structurally: **a folded group spans dtypes.** A
 * `pack-quantized` projection is an `I32` payload plus a `BF16` scale plane
 * plus an `I64` shape, one logical tensor across three rows. There is no
 * dtype to attribute its parameter count to, so a per-dtype ledger has no
 * choice but to count what each dtype physically holds.
 *
 * That also makes the `32.00` in the `I32` row right rather than embarrassing:
 * this table answers "where do the bytes live", and the bpw column is a storage
 * width by construction. The model's cost per weight is the map's job, and the
 * two disagreeing is the packing being visible, not a contradiction.
 *
 * What *was* wrong is what the column claimed: its explanation said these are
 * "the values the model learned", which for a packed row they are not. See
 * `ui/explain.ts`.
 *
 * ## And a second divergence, which has nothing to do with packing
 *
 * Non-parameter buffers (`analysis/buffers.ts`) make this count exceed the
 * model's parameters on checkpoints where nothing is packed at all.
 * `openai-community/gpt2` is F32 end to end and stores twelve `1×1×1024×1024`
 * causal masks: 137M elements against 124,439,808 parameters, with no packing
 * anywhere to explain the gap.
 *
 * The ledger is still right to count them — they occupy those bytes, and "where
 * do the bytes live" is the question it answers. What it may not do is call
 * them parameters, which is why the field and the column say `elements`.
 */
export function tallyByDtype(tensors: readonly InventoryTensor[]): DtypeTally[] {
  const byDtype = new Map<
    string,
    { tensors: number; storedElements: number; bytes: number }
  >();
  for (const t of tensors) {
    const tally = byDtype.get(t.dtype) ?? {
      tensors: 0,
      storedElements: 0,
      bytes: 0,
    };
    tally.tensors += 1;
    tally.storedElements += t.numel;
    tally.bytes += t.byteLength;
    byDtype.set(t.dtype, tally);
  }
  return [...byDtype.entries()]
    .map(([dtype, tally]) => ({ dtype, ...tally }))
    .sort((a, b) => b.bytes - a.bytes);
}
